import { getFileUploadUrl, uploadFileToS3 } from '@/shared/lib/file-upload';

/**
 * Outcome of uploading a single intake file.
 *
 * On success: `asset` carries the asset id assigned by the presign endpoint
 * and `error` is `null`.
 * On failure: `asset` is `null` and `error` carries the cause. The caller
 * decides whether to surface, retry, or ignore the row.
 */
export interface IntakeFileResult {
  file: File;
  asset: IntakeUploadedAsset | null;
  error: Error | null;
}

export interface IntakeUploadedAsset {
  assetId: string;
  mimeType: string;
  metadata: {
    fileName: string;
    size: number;
  };
}

/** Lifecycle states reported via the optional `onFileProgress` callback. */
export type IntakeFileProgressState = 'uploading' | 'done' | 'error';

const toError = (cause: unknown): Error =>
  cause instanceof Error ? cause : new Error(String(cause));

/**
 * Upload a single file to S3 via the presign-then-POST flow.
 *
 * Errors that are NOT aborts are caught and returned in
 * {@link IntakeFileResult.error}, mirroring the parallel batch contract — so
 * an in-form retry can branch on `result.error` without a try/catch.
 *
 * `AbortError` is rethrown so callers can stop the entire batch (and any
 * downstream work) on user cancellation. The intake flow treats abort as
 * "user cancelled, stop everything" rather than a per-file failure.
 *
 * @param file - The browser-native File to upload.
 * @param signal - Shared abort signal from the orchestrator's
 *   `AbortController`. Forwarded to the S3 POST so an in-flight upload can
 *   be cancelled.
 */
export const uploadOneFile = async (file: File, signal: AbortSignal): Promise<IntakeFileResult> => {
  try {
    const { url, fields, assetId } = await getFileUploadUrl(file.type, file.name);
    const formData = new FormData();
    Object.entries(fields).forEach(([key, value]) => {
      formData.append(key, value);
    });
    formData.append('file', file);
    await uploadFileToS3(url, formData, signal);
    return {
      file,
      asset: {
        assetId,
        mimeType: file.type || 'application/octet-stream',
        metadata: {
          fileName: file.name,
          size: file.size,
        },
      },
      error: null,
    };
  } catch (cause) {
    const error = toError(cause);
    if (error.name === 'AbortError') {
      // Bubble aborts so the orchestrator can stop everything in one place.
      throw error;
    }
    return { file, asset: null, error };
  }
};

/**
 * Upload all intake files in parallel.
 *
 * Each file is independent: a single failure does NOT reject the batch —
 * `Promise.all` resolves with one {@link IntakeFileResult} per file, and
 * the caller inspects `result.error` to decide what to do with each row
 * (continue with the successful subset, surface the failures, etc.).
 *
 * `AbortError` is the one exception: it propagates through `Promise.all`
 * because abort means "user cancelled the run" — not a per-file failure to
 * recover from.
 *
 * @param files - The list of intake files. Empty array short-circuits with
 *   `[]` and skips the network entirely.
 * @param signal - Shared abort signal forwarded to each per-file upload.
 * @param onFileProgress - Optional progress callback. Called with
 *   `(idx, 'uploading')` when each file's upload starts, then
 *   `(idx, 'done')` on success or `(idx, 'error', err)` on failure.
 */
export const uploadIntakeFiles = async (
  files: File[],
  signal: AbortSignal,
  onFileProgress?: (idx: number, state: IntakeFileProgressState, err?: Error) => void,
): Promise<IntakeFileResult[]> => {
  if (files.length === 0) {
    return [];
  }
  return Promise.all(
    files.map(async (file, idx) => {
      onFileProgress?.(idx, 'uploading');
      try {
        const result = await uploadOneFile(file, signal);
        onFileProgress?.(idx, result.error ? 'error' : 'done', result.error ?? undefined);
        return result;
      } catch (cause) {
        // Only abort errors reach here — uploadOneFile catches the rest.
        const error = toError(cause);
        onFileProgress?.(idx, 'error', error);
        throw error;
      }
    }),
  );
};
